import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useThunk } from "../hooks/useThunk";
import { updatePassword, updateAuth } from "../store";

import showToast from "../utils/toastNotifications";

// Hooks
import useForm from "../hooks/useForm";
// Constants
import { COMMON_FIELD_TYPES } from "../constants/fieldTypes";
// Icons
import { Eye, EyeOff } from "lucide-react";
//Components
import CustomInput from "../components/Form/Input";

const Profile = () => {
  const { user } = useSelector((state) => state.auth);
  const auth = JSON.parse(localStorage.getItem("auth"));
  const token = auth?.token;
  const name =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User";

  const dispatch = useDispatch();
  const [doResetPassword, isResettingPassword] = useThunk(updatePassword);

  const initialValues = {
    fullName: name,
    email: user?.email || "",
    role: user?.roles || "",
    password: "",
    confirm_password: "",
  };

  const [requiredFields, setRequiredFields] = useState({
    fullName: false,
    email: true,
    role: false,
    password: false,
    confirm_password: false,
  });

  const inputTypes = {
    fullName: { type: COMMON_FIELD_TYPES.FULLNAME },
    email: { type: COMMON_FIELD_TYPES.EMAIL_FIELD },
    role: { type: COMMON_FIELD_TYPES.TEXT_FIELD },
    password: { type: COMMON_FIELD_TYPES.PASSWORD_FIELD },
    confirm_password: { type: COMMON_FIELD_TYPES.CONFIRM_PASSWORD },
  };
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    clearError,
    validateForm,
  } = useForm(initialValues, requiredFields, inputTypes);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState("");
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setRequiredFields((prev) => ({
      ...prev,
      confirm_password: formData.password.trim().length > 0,
    }));
  }, [formData.password]);

  const isFormChanged = () => {
    return Object.keys(initialValues).some(
      (key) => formData[key] !== initialValues[key]
    );
  };

  useEffect(() => {
    const formIsValid = validateForm();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

    setIsValid(formIsValid && emailValid && isFormChanged());
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast("error", "Please fix validation errors.");
      return;
    }

    const { password, confirm_password, fullName } = formData;
    const [firstName, ...lastNameParts] = fullName.trim().split(" ");
    const lastName = lastNameParts.join(" ");

    if (password !== confirm_password) {
      showToast("error", "Passwords do not match.");
      return;
    }

    // Construct only changed fields
    const data = {};

    if (fullName !== initialValues.fullName) {
      data.firstName = firstName;
      data.lastName = lastName;
    }

    if (password && password !== initialValues.password) {
      data.password = password;
    }

    if (Object.keys(data).length === 0) {
      showToast("info", "No changes to save.");
      return;
    }

    const payload = {
      id: user?.id,
      token: token,
      data,
    };

    const results = await doResetPassword(payload);

    if (results.success) {
      handleInputChange("password", "");
      handleInputChange("confirm_password", "");
      showToast("success", results.message || "Updated successfully.");
      if (data.firstName || data.lastName) {
        const updatedUser = {
          ...user,
          ...(data.firstName && { firstName: data.firstName }),
          ...(data.lastName && { lastName: data.lastName }),
        };

        dispatch(updateAuth({ user: updatedUser }));

        const updatedAuth = {
          ...auth,
          user: updatedUser,
        };
        localStorage.setItem("auth", JSON.stringify(updatedAuth));
      }
    } else {
      showToast("error", results.message || "Failed to update.");
    }
  };

  return (
    <div className="flex justify-center items-center ">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col justify-center items-center md:shadow-[4px_4px_32px_rgba(36,132,147,0.2)] rounded-2xl w-[390px] lg:w-[824px] xl:w-[1240px] h-[740px] space-y-[50px] font-poppins mt-[40px] mb-[80px] lg:mx-[100px] lg:mt-[50px] lg:mb-[100px]"
      >
        {/* <img src={profile} alt="profile avatar" className="w-28" /> */}
        <div className="flex flex-col items-start w-[280px] xs:w-[335px] lg:w-[650px] xl2:w-[700px]">
          <CustomInput
            label="Full Name"
            type="text"
            value={formData.fullName}
            onChange={(e) => handleInputChange("fullName", e.target.value)}
            onBlur={(e) => handleBlur("fullName", e.target.value)}
            onFocus={() => clearError("fullName")}
            labelStyle="text-level-6 font-medium text-[#6A6868] font-inter"
            inputStyle="h-[40px] w-[280px] xs:w-[335px] lg:w-[650px] xl2:w-[700px] border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none"
            error={touched.fullName && errors.fullName}
          />

          <CustomInput
            label="Email"
            type="email"
            value={formData.email}
            // onChange={(e) => handleInputChange("email", e.target.value)}
            // onBlur={(e) => handleBlur("email", e.target.value)}
            // onFocus={() => clearError("email")}
            // labelStyle="text-level-6 font-medium text-[#344054] font-inter"
            inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
            // error={touched.email && errors.email}
            readOnly
          />

          <CustomInput
            label="Role"
            type="text"
            value={formData.role}
            // onChange={(e) => handleInputChange("role", e.target.value)}
            // onBlur={(e) => handleBlur("role", e.target.value)}
            // onFocus={() => clearError("role")}
            // labelStyle="text-level-6 font-medium text-[#344054] font-inter"
            inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
            // error={touched.role && errors.role}
            readOnly
          />

          <CustomInput
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={(e) => {
              handleBlur("password", e.target.value);
              setFocusedField("");
            }}
            onFocus={() => {
              clearError("password");
              setFocusedField("Password");
            }}
            labelStyle="text-level-6 font-medium text-[#344054] font-inter"
            inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
            icon={
              (focusedField === "password" || formData.password) && (
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              )
            }
            error={touched.password && errors.password}
          />
          {formData.password && (
            <CustomInput
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirm_password}
              onChange={(e) =>
                handleInputChange("confirm_password", e.target.value)
              }
              onBlur={(e) => {
                handleBlur("confirm_password", e.target.value);
                setFocusedField("");
              }}
              onFocus={() => {
                clearError("confirm_password");
                setFocusedField("confirm_Password");
              }}
              labelStyle="text-level-6 font-medium text-[#344054] font-inter"
              inputStyle="h-[40px] w-full border border-[#E6E7EC] rounded-xl bg-[#E0F2F14D] px-4 focus:outline-none placeholder:!text-[#334155] placeholder:text-level-6 xl:placeholder:text-level-5 placeholder:font-normal"
              icon={
                (focusedField === "confirm_password" ||
                  formData.confirm_password) && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-3 text-gray-500"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                )
              }
              error={touched.confirm_password && errors.confirm_password}
            />
          )}
        </div>

        <button
          type="submit"
          disabled={!isValid || isResettingPassword}
          className={`rounded-xl w-[97px] h-[43px] lg:w-[122px] font-medium text-level-5 transition-opacity duration-300 ${
            isValid
              ? "bg-[#FF7361] text-white"
              : "opacity-0 pointer-events-none"
          }`}
        >
          {isResettingPassword ? "Saving..." : "Save"}
        </button>
      </form>
    </div>
  );
};
export default Profile;
